// InvitationsLauncherContext — opens the candidate's invitations as a right-side
// slide-over from anywhere (the Home "Du har en inbjudan" card + the bell).
// Invitations are episodic and not part of "Jobb", so they have no nav home —
// they pop over the current page, you act, and close.
import React, { createContext, useCallback, useContext, useState } from 'react';
import { X, Mail } from 'lucide-react';
import { IslandSlideOver } from '@/components/custom-ui/IslandSlideOver';
import { IconChip } from '@/components/custom-ui/IconChip';
import ReceivedInvitationsPage from '@/components/dashboard/candidate/ReceivedInvitationsPage';

interface InvitationsLauncherValue {
    openInvitations: () => void;
}

const InvitationsLauncherContext = createContext<InvitationsLauncherValue | null>(null);

export const useInvitationsLauncher = (): InvitationsLauncherValue => {
    const ctx = useContext(InvitationsLauncherContext);
    if (!ctx) throw new Error('useInvitationsLauncher must be used within an InvitationsLauncherProvider');
    return ctx;
};

export const InvitationsLauncherProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [open, setOpen] = useState(false);
    // Bump on each open so the list remounts and refetches fresh.
    const [nonce, setNonce] = useState(0);
    const openInvitations = useCallback(() => {
        setNonce((n) => n + 1);
        setOpen(true);
    }, []);

    return (
        <InvitationsLauncherContext.Provider value={{ openInvitations }}>
            {children}
            {open && (
                <IslandSlideOver onClose={() => setOpen(false)} widthClass="max-w-3xl" zClass="z-[60]">
                    <div className="flex items-center justify-between gap-3 p-5 border-b border-gray-100 flex-shrink-0">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <IconChip icon={Mail} size="sm" tone="brand" />
                            <div className="min-w-0">
                                <h2 className="text-lg font-bold text-gray-900">Mina inbjudningar</h2>
                                <p className="text-xs text-gray-500 truncate">Inbjudningar du har fått från arbetsgivare</p>
                            </div>
                        </div>
                        <button onClick={() => setOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0">
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto min-h-0 p-5">
                        <ReceivedInvitationsPage key={nonce} />
                    </div>
                </IslandSlideOver>
            )}
        </InvitationsLauncherContext.Provider>
    );
};
